import { Settings } from '@streamr/tv-engine/~/settings';
import { addMediaStreamTrackStopListener } from '../../utils/addMediaStreamTrackStopListener';
import * as audioRecorderProcessor from '../mediaproducer/workers/webaudiorecorder.worklet';
import { Timekeeper } from './Timekeeper';
import { Track } from './Track';
export class AudioFrameExtractor {
    static LOG_PREFIX = '[AudioFrameExtractor]';
    static initialized = false;
    static audioContext;
    track;
    trackId;
    audioSource;
    audioWorklet;
    frameCounter = 0;
    isStopped = false;
    removeTrackStopListener;
    timestampOffsetMicros;
    outputTrack;
    // Sample-based timestamp tracking (accurate to actual audio content)
    cumulativeSampleCount = 0;
    constructor(track) {
        this.track = track;
        this.trackId = track.id;
        this.outputTrack = new Track(track.id, true);
    }
    static async initialize() {
        if (AudioFrameExtractor.initialized)
            return;
        AudioFrameExtractor.audioContext = new AudioContext({
            sampleRate: Settings.audioSampleRate,
        });
        await AudioFrameExtractor.audioContext.audioWorklet.addModule(
        // biome-ignore lint/suspicious/noExplicitAny: @todo fix types (the "any")
        audioRecorderProcessor);
        AudioFrameExtractor.initialized = true;
    }
    /**
     * Resume the extractor's AudioContext (e.g. after a user gesture satisfies autoplay policy).
     */
    static async resumeContext() {
        if (AudioFrameExtractor.audioContext) {
            await AudioFrameExtractor.audioContext.resume();
        }
    }
    /**
     * Suspend the extractor's AudioContext.
     */
    static async suspendContext() {
        if (AudioFrameExtractor.audioContext) {
            await AudioFrameExtractor.audioContext.suspend();
        }
    }
    /**
     * Close and release the static AudioContext created by initialize().
     * Must be called during engine shutdown to prevent resource leaks
     * across stop/start cycles.
     */
    static async teardown() {
        if (AudioFrameExtractor.audioContext) {
            console.log(`${AudioFrameExtractor.LOG_PREFIX} teardown: closing static AudioContext`);
            await AudioFrameExtractor.audioContext.close();
            AudioFrameExtractor.audioContext = undefined;
        }
        AudioFrameExtractor.initialized = false;
    }
    start() {
        if (!AudioFrameExtractor.initialized) {
            throw new Error('AudioFrameExtractor not yet initialized');
        }
        this.startFrameExtraction();
    }
    startFrameExtraction() {
        if (!AudioFrameExtractor.audioContext) {
            throw new Error('AudioContext not initialized');
        }
        // Create media stream source
        this.audioSource =
            AudioFrameExtractor.audioContext.createMediaStreamSource(new MediaStream([this.track]));
        // Create and configure worklet
        this.audioWorklet = new AudioWorkletNode(AudioFrameExtractor.audioContext, 'audio-recorder-processor');
        // Handle messages from the audio worklet
        this.audioWorklet.port.onmessage = (event) => {
            if (this.isStopped)
                return;
            if (event.data.samples) {
                const samples = event.data.samples;
                const numChannels = event.data.numChannels ?? 1;
                const framesPerChannel = samples.length / numChannels;
                const sampleRate = Settings.audioSampleRate;
                const timestampMicros = Math.round((this.cumulativeSampleCount / sampleRate) * 1_000_000);
                const durationMicros = Math.round((framesPerChannel / sampleRate) * 1_000_000);
                this.cumulativeSampleCount += framesPerChannel;
                const frame = this.createUnencodedAudioFrame(timestampMicros, durationMicros, samples);
                this.outputTrack.emit(frame, false, [frame.data.buffer]);
            }
        };
        // Connect the audio graph
        this.audioSource.connect(this.audioWorklet);
        this.audioWorklet.connect(AudioFrameExtractor.audioContext.destination);
        // Listen for track stop/end to trigger cleanup
        this.removeTrackStopListener = addMediaStreamTrackStopListener(this.track, () => {
            console.log('AudioFrameExtractor: track stopped/ended');
            this.cleanup();
        });
    }
    cleanup() {
        if (this.isStopped)
            return;
        this.isStopped = true;
        console.log('AudioFrameExtractor: cleaning up');
        // Disconnect audio nodes
        if (this.audioSource) {
            this.audioSource.disconnect();
            this.audioSource = undefined;
        }
        if (this.audioWorklet) {
            this.audioWorklet.port.onmessage = null;
            this.audioWorklet.disconnect();
            this.audioWorklet = undefined;
        }
        // Remove track stop listener
        if (this.removeTrackStopListener) {
            this.removeTrackStopListener();
            this.removeTrackStopListener = undefined;
        }
        this.outputTrack.emit(undefined, true);
        this.outputTrack.unsetListener();
        console.log('AudioFrameExtractor: cleanup complete');
    }
    createUnencodedAudioFrame(contentTimestampMicros, durationMicros, data) {
        if (this.timestampOffsetMicros === undefined) {
            this.timestampOffsetMicros =
                Timekeeper.getInstance('producer').getLastTimestampMicros();
        }
        const timestampMicros = BigInt(contentTimestampMicros) + this.timestampOffsetMicros;
        return {
            frameNumber: BigInt(this.frameCounter++),
            timestamp: timestampMicros,
            duration: BigInt(durationMicros),
            trackId: this.trackId ?? '',
            data: data,
            done: false,
            isKeyFrame: true,
        };
    }
    /**
     * Stop extraction and clean up resources.
     */
    stop() {
        if (this.isStopped) {
            return Promise.resolve();
        }
        console.log('AudioFrameExtractor: stop() called');
        this.cleanup();
        return Promise.resolve();
    }
    getOutputTrack() {
        return this.outputTrack;
    }
    getTrackId() {
        return this.trackId;
    }
    getAudioWorkletNode() {
        return this.audioWorklet;
    }
}
